import { useCallback, useEffect, useRef, useState } from "react";

import { getMenuCategories } from "services/menu/rest";
import {
    MenuCategoriesPageDto,
    MenuCategoryDto,
    MenuScopeSummaryDto,
} from "services/menu/restModels";

const EMPTY_SUMMARY: MenuScopeSummaryDto = {
    categoriesCount: 0,
    itemsCount: 0,
    stopListItemsCount: 0,
    stopListCategoriesCount: 0,
};

export interface UseMenuCategoriesResult {
    categories: MenuCategoryDto[];
    summary: MenuScopeSummaryDto;
    loading: boolean;
    error: boolean;
    refetch: () => void;
    // Точечная правка счётчика стоп-листа у категории (+1 / −1) без перезапроса списка.
    adjustStopListCount: (menuCategoryId: number, delta: number) => void;
    setCategoryStopList: (menuCategoryId: number, isInStopList: boolean) => void;
}

/*
* Категории меню одним лёгким запросом: имена, расписание и счётчики позиций, но не сами
* позиции. Это замена getMenuById, который тянул меню целиком (на 800+ позиций — мегабайты)
* и перезапрашивался после каждой правки.
* Кеша на уровне модуля тут нет намеренно: категории меняются прямо на этом экране, и
* устаревший список сразу бы разошёлся со счётчиками.
* */
export const useMenuCategories = (menuId: number): UseMenuCategoriesResult => {
    const [page, setPage] = useState<MenuCategoriesPageDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [reloadKey, setReloadKey] = useState<number>(0);
    // Меню, для которого данные уже получены: отличает первую загрузку от фоновой.
    const loadedMenuIdRef = useRef<number | null>(null);

    const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

    /*
    * Тумблер стоп-листа на плитке меняет ровно одну цифру в сайдбаре. Перезапрашивать
    * ради неё весь список категорий нельзя: экран уходил бы в скелетон и сбрасывал
    * прокрутку к началу на каждое переключение.
    * */
    const adjustStopListCount = useCallback((menuCategoryId: number, delta: number) => {
        setPage((current) => {
            if (!current) return current;

            return {
                categories: current.categories.map((item) =>
                    item.id === menuCategoryId
                        ? {
                              ...item,
                              stopListItemsCount: Math.max(0, item.stopListItemsCount + delta),
                          }
                        : item
                ),
                summary: {
                    ...current.summary,
                    stopListItemsCount: Math.max(0, current.summary.stopListItemsCount + delta),
                },
            };
        });
    }, []);

    /*
    * Стоп-лист категории правится и с экрана меню, и со стоп-листа. Ответ ручки содержит
    * ровно те поля, что здесь меняются, поэтому перезапрос списка после успеха не нужен —
    * а на экране стоп-листа он ещё и сдёрнул бы строку до ответа сервера.
    * */
    const setCategoryStopList = useCallback((menuCategoryId: number, isInStopList: boolean) => {
        setPage((current) => {
            if (!current) return current;

            const categories = current.categories.map((item) =>
                item.id === menuCategoryId ? { ...item, isInStopList } : item
            );

            return {
                categories,
                summary: {
                    ...current.summary,
                    stopListCategoriesCount: categories.filter((item) => item.isInStopList).length,
                },
            };
        });
    }, []);

    useEffect(() => {
        // ignore-флаг: поздний ответ по старому menuId не перезапишет свежий.
        let ignore = false;

        // Скелетон — только на первой загрузке этого меню. Фоновое обновление не гасит
        // экран: иначе любое обновление данных выглядело бы как полная перезагрузка.
        if (loadedMenuIdRef.current !== menuId) {
            setLoading(true);
        }

        setError(false);

        getMenuCategories(menuId)
            .then((result) => {
                if (ignore) return;
                if (result) {
                    setPage(result);
                    loadedMenuIdRef.current = menuId;
                } else {
                    setError(true);
                    setPage(null);
                }
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setPage(null);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, reloadKey]);

    return {
        categories: page?.categories || [],
        summary: page?.summary || EMPTY_SUMMARY,
        loading,
        error,
        refetch,
        adjustStopListCount,
        setCategoryStopList,
    };
};
